import React, { useContext, useEffect } from 'react'
import { PackageOptionView } from 'selectors/cart/cartSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import LePackageUpgradeCard from 'components/Booking/Browse/PackageUpgrade/LePackageUpgradeCard'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import * as Analytics from 'analytics/analytics'
import { upgradePackageEvent } from 'analytics/optimizely/events'
import PackageUpgradeModal from 'components/Booking/Browse/PackageUpgrade/PackageUpgradeModal'
import ModalContext from 'contexts/ModalContext'

export const OfferUpgradeModalContext = React.createContext<(upgrade: OfferPackageOptionView, packageOption: OfferPackageOptionView, hasFlightsSelected: boolean) => Promise<OfferPackageOptionView>>(undefined)

interface Props {
  children: React.ReactNode;
  offer: App.Offer;
}

interface UpgradeModalState {
  selectedPackage?: OfferPackageOptionView;
  upgradePackage?: OfferPackageOptionView;
  image?: App.Image;
  upgradePackageView?: PackageOptionView;
  hasFlightsSelected?: boolean;
}

function OfferUpgradeModalContextProvider({ children, offer }: Props) {
  const [isOpen, setOpenModal] = React.useState<boolean>(false)
  const [upgradeModal, setUpgradeModal] = React.useState<UpgradeModalState>({})

  const awaitingPromiseRef = React.useRef<{
    resolve:(resolvePackageOptionView: OfferPackageOptionView) => void;
    reject: () => void;
  }>()

  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const openModal = React.useCallback((upgrade: OfferPackageOptionView, packageOption: OfferPackageOptionView, hasFlightsSelected: boolean): Promise<OfferPackageOptionView> => {
    setOpenModal(true)
    setUpgradeModal({
      selectedPackage: packageOption,
      upgradePackage: upgrade,
      image: { id: packageOption.package.roomType?.images?.[0]?.id },
      hasFlightsSelected,
      upgradePackageView: {
        offer,
        // no need to account for flight prices, they are always the same for the same day
        priceDifference: upgrade.hotelPrice - packageOption.hotelPrice,
        memberPriceDifference: upgrade.hotelMemberPrice - packageOption.hotelMemberPrice,
        price: upgrade.hotelPrice,
        memberPrice: upgrade.hotelMemberPrice,
        package: upgrade.package,
        value: upgrade.hotelValue,
        flightPrice: upgrade.flightPrice,
        flightsAvailable: upgrade.flightsAvailable,
      },
    })
    return new Promise((resolve, reject) => {
      awaitingPromiseRef.current = { resolve, reject }
    })
  }, [offer])

  const onUpgrade = () => {
    setOpenModal(false)
    trackEvent('UPGRADE_MODAL_ACCEPTED')
    awaitingPromiseRef.current?.resolve(upgradeModal.upgradePackage)
    Analytics.track('upgrade-package', upgradePackageEvent())
  }

  const onNoThanks = () => {
    setOpenModal(false)
    trackEvent('UPGRADE_MODAL_REJECTED')
    if (upgradeModal.selectedPackage?.capacityValid && upgradeModal.selectedPackage.hotelAvailable) {
      awaitingPromiseRef.current?.resolve(upgradeModal.selectedPackage)
    }
  }

  const showModal = useContext(ModalContext)

  useEffect(() => {
    if (isOpen && upgradeModal.upgradePackageView && upgradeModal.selectedPackage) {
      showModal(<PackageUpgradeModal
        onNoThanks={onNoThanks}
        onUpgrade={onUpgrade}
      >
        <LePackageUpgradeCard
          packageUpgrade={upgradeModal.upgradePackageView}
          selectedPackage={upgradeModal.selectedPackage}
          hasFlightsSelected={upgradeModal.hasFlightsSelected}
        />
      </PackageUpgradeModal>)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, upgradeModal.upgradePackageView, upgradeModal.selectedPackage])

  return (<OfferUpgradeModalContext.Provider
      value={openModal}
      children={children}
    />
  )
}

export default withOfferPageAndReduxState((state) => ({
  offer: state.offer,
}))(OfferUpgradeModalContextProvider)
